/**
 * Observations List Component
 * Displays submitted observations grouped by date with map view
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import mapboxgl from 'mapbox-gl';
import * as observationsApi from '../../services/observationsApi';
import EmptyState from '../common/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import { useParticipantLabels } from '../../hooks/useParticipantLabels';
import ItineraryMapGL from '@signals/coordination-itinerary-ui/components/ItineraryMapGL';

interface ObservationsListProps {
  onEditObservation?: (observation: observationsApi.Observation) => void;
  mapWidth?: number;
  onResizeStart?: (e: React.MouseEvent) => void;
  isDragging?: boolean;
  isActive?: boolean;
}

const ObservationsList: React.FC<ObservationsListProps> = ({ 
  onEditObservation,
  mapWidth = 65,
  onResizeStart,
  isDragging = false,
  isActive = true,
}) => {
  const { singular } = useParticipantLabels();
  const { user } = useAuth();
  const usernameLower = user?.username?.toLowerCase() || '';
  const isStudent = user?.roles?.includes('student');
  const isTrainee = usernameLower.includes('trainee');
  const isStudentInUsername = usernameLower.includes('student');
  const isStudentOrTrainee = isStudent || isTrainee || isStudentInUsername;
  
  // Map reference
  const mapRef = useRef<mapboxgl.Map | null>(null);
  
  // Helper function to extract student number from username
  const extractStudentNumber = (username: string): string | null => {
    if (!username) return null;
    
    const studentMatch = username.match(/Student[_-]?(\d+)/i);
    const traineeMatch = username.match(/Trainee[_-]?(\d+)/i);
    
    if (traineeMatch) {
      return `Trainee-${traineeMatch[1].padStart(2, '0')}`;
    }
    if (studentMatch) {
      return `Student-${studentMatch[1].padStart(2, '0')}`;
    }
    
    return null;
  };
  
  const userStudentNumber = isStudentOrTrainee && user?.username ? 
    extractStudentNumber(user.username) || '' 
    : '';
  const isAdminOrCadre = !isStudentOrTrainee; // Admin/cadre can search
  
  const [observations, setObservations] = useState<observationsApi.Observation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [observationToDelete, setObservationToDelete] = useState<observationsApi.Observation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState<number | null>(null);

  // Cadre note edit dialog state - only used by admin/cadre users
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteObservation, setNoteObservation] = useState<observationsApi.Observation | null>(null);
  const [noteDraft, setNoteDraft] = useState<string>('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  
  // Search filters for admin/cadre
  const [studentFilter, setStudentFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Quick date-range helpers: "Today", "7 Days", "30 Days", "All"
  const applyQuickRange = (days: number | null) => {
    if (days === null) {
      setStartDate('');
      setEndDate('');
      return;
    }
    const end = new Date();
    const start = new Date();
    // `days === 1` means today only; otherwise include the last N-1 days + today
    start.setDate(end.getDate() - Math.max(0, days - 1));
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  // Determine which quick-range (if any) currently matches the selected dates
  const activeQuickRange = React.useMemo<'today' | '7d' | '30d' | 'all' | null>(() => {
    if (!startDate && !endDate) return 'all';
    if (!startDate || !endDate) return null;
    const today = format(new Date(), 'yyyy-MM-dd');
    if (endDate !== today) return null;
    const s = new Date(startDate);
    const e = new Date(endDate);
    const diffDays = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays === 1) return 'today';
    if (diffDays === 7) return '7d';
    if (diffDays === 30) return '30d';
    return null;
  }, [startDate, endDate]);
  
  // Map interaction state
  const [selectedObservationId, setSelectedObservationId] = useState<number | null>(null);
  const [highlightedPointId, setHighlightedPointId] = useState<string | null>(null);
  const [flashTrigger, setFlashTrigger] = useState<string | null>(null);
  
  // Get all vehicle and individual observations for map
  const allVehicleObs = observations.flatMap(obs => 
    obs.vehicle_observations.map(v => ({ ...v, observation_id: obs.id, student_number: obs.student_number }))
  );
  const allIndividualObs = observations.flatMap(obs => 
    obs.individual_observations.map(i => ({ ...i, observation_id: obs.id, student_number: obs.student_number }))
  );
  
  // Filter observations for map based on selected observation
  const filteredVehicleObs = selectedObservationId 
    ? allVehicleObs.filter(v => v.observation_id === selectedObservationId)
    : allVehicleObs;
  const filteredIndividualObs = selectedObservationId
    ? allIndividualObs.filter(i => i.observation_id === selectedObservationId)
    : allIndividualObs;

  const collectMapCoordinates = React.useCallback((
    vehicles: typeof allVehicleObs,
    individuals: typeof allIndividualObs
  ): [number, number][] => {
    const coordinates: [number, number][] = [];
    vehicles.forEach(v => {
      if (v.latitude !== 0 && v.longitude !== 0) {
        coordinates.push([v.longitude, v.latitude]);
      }
    });
    individuals.forEach(i => {
      if (i.latitude !== 0 && i.longitude !== 0) {
        coordinates.push([i.longitude, i.latitude]);
      }
    });
    return coordinates;
  }, []);

  const fitMapToCoordinates = React.useCallback((coordinates: [number, number][]) => {
    if (!mapRef.current || coordinates.length === 0) return;

    if (coordinates.length === 1) {
      mapRef.current.flyTo({
        center: coordinates[0],
        zoom: 14,
        duration: 1500,
      });
      return;
    }

    const bounds = coordinates.reduce((nextBounds, coord) => {
      return nextBounds.extend(coord);
    }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

    mapRef.current.fitBounds(bounds, {
      padding: 80,
      duration: 1500,
      maxZoom: 14,
    });
  }, []);

  const fitMapToAllObservations = React.useCallback(() => {
    fitMapToCoordinates(collectMapCoordinates(allVehicleObs, allIndividualObs));
  }, [allVehicleObs, allIndividualObs, collectMapCoordinates, fitMapToCoordinates]);

  const clearObservationSelection = React.useCallback(() => {
    setSelectedObservationId(null);
    setHighlightedPointId(null);
    setFlashTrigger(null);
    requestAnimationFrame(() => {
      fitMapToAllObservations();
    });
  }, [fitMapToAllObservations]);
  
  // Handle click on list item to zoom map
  const handleListItemClick = (observation: observationsApi.Observation, pointType?: 'vehicle' | 'individual', pointIndex?: number) => {
    const isHeaderClick = pointType === undefined && pointIndex === undefined;

    // Toggle off when clicking the same observation header or the same map point again
    if (isHeaderClick && selectedObservationId === observation.id) {
      clearObservationSelection();
      return;
    }

    if (pointType === 'vehicle' && pointIndex !== undefined) {
      const vehicle = observation.vehicle_observations[pointIndex];
      const flashId = `vehicle-${vehicle?.id}`;
      if (highlightedPointId === flashId && selectedObservationId === observation.id) {
        clearObservationSelection();
        return;
      }
    } else if (pointType === 'individual' && pointIndex !== undefined) {
      const individual = observation.individual_observations[pointIndex];
      const flashId = `individual-${individual?.id}`;
      if (highlightedPointId === flashId && selectedObservationId === observation.id) {
        clearObservationSelection();
        return;
      }
    }
    
    setSelectedObservationId(observation.id || null);
    
    // If clicking on a specific point, zoom to it and flash
    if (pointType === 'vehicle' && pointIndex !== undefined) {
      const vehicle = observation.vehicle_observations[pointIndex];
      const targetLat = vehicle?.latitude;
      const targetLon = vehicle?.longitude;
      const flashId = `vehicle-${vehicle?.id}`;
      setHighlightedPointId(flashId);
      setFlashTrigger(prev => prev === flashId ? flashId + '.' : flashId);
      
      if (mapRef.current && targetLat !== 0 && targetLon !== 0) {
        mapRef.current.flyTo({
          center: [targetLon, targetLat],
          zoom: 16,
          duration: 1500,
        });
      }
    } else if (pointType === 'individual' && pointIndex !== undefined) {
      const individual = observation.individual_observations[pointIndex];
      const targetLat = individual?.latitude;
      const targetLon = individual?.longitude;
      const flashId = `individual-${individual?.id}`;
      setHighlightedPointId(flashId);
      setFlashTrigger(prev => prev === flashId ? flashId + '.' : flashId);
      
      if (mapRef.current && targetLat !== 0 && targetLon !== 0) {
        mapRef.current.flyTo({
          center: [targetLon, targetLat],
          zoom: 16,
          duration: 1500,
        });
      }
    } else {
      // Clicking on accordion header - zoom to extent of all points in this observation
      setHighlightedPointId(null);
      
      // Flash the first point with valid coords to draw attention
      const firstVeh = observation.vehicle_observations.find(v => v.latitude !== 0 && v.longitude !== 0);
      const firstInd = observation.individual_observations.find(i => i.latitude !== 0 && i.longitude !== 0);
      if (firstVeh) {
        const flashId = `vehicle-${firstVeh.id}`;
        setFlashTrigger(prev => prev === flashId ? flashId + '.' : flashId);
      } else if (firstInd) {
        const flashId = `individual-${firstInd.id}`;
        setFlashTrigger(prev => prev === flashId ? flashId + '.' : flashId);
      }
      
      const coordinates = collectMapCoordinates(
        observation.vehicle_observations.map(v => ({ ...v, observation_id: observation.id, student_number: observation.student_number })),
        observation.individual_observations.map(i => ({ ...i, observation_id: observation.id, student_number: observation.student_number }))
      );

      if (mapRef.current && coordinates.length === 1) {
        mapRef.current.flyTo({ center: coordinates[0], zoom: 16, duration: 1500 });
      } else if (mapRef.current && coordinates.length > 1) {
        const bounds = coordinates.reduce(
          (nextBounds, coord) => nextBounds.extend(coord),
          new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
        );
        mapRef.current.fitBounds(bounds, { padding: 100, duration: 1500, maxZoom: 16 });
      }
    }
  };
  
  // Load observations - memoized to prevent infinite re-render loops
  const loadObservations = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await observationsApi.getObservations(1, 100);
      
      let filteredObservations = response.items;
      
      // Students see only their own
      if (isStudentOrTrainee) {
        filteredObservations = filteredObservations.filter(obs => obs.student_number === userStudentNumber);
      } else {
        // Admin/cadre can filter by student
        if (studentFilter) {
          filteredObservations = filteredObservations.filter(obs =>
            obs.student_number?.toLowerCase().includes(studentFilter.toLowerCase())
          );
        }
        
        // Filter by date range
        if (startDate || endDate) {
          filteredObservations = filteredObservations.filter(obs => {
            if (!obs.observation_date) return false;
            const obsDate = format(parseISO(obs.observation_date), 'yyyy-MM-dd');
            const isAfterStart = !startDate || obsDate >= startDate;
            const isBeforeEnd = !endDate || obsDate <= endDate;
            return isAfterStart && isBeforeEnd;
          });
        }
      }
      
      setObservations(filteredObservations);
    } catch (err) {
      console.error('Error loading observations:', err);
      setError('Failed to load observations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isStudentOrTrainee, userStudentNumber, studentFilter, startDate, endDate]);
  
  // Load observations when filters change
  useEffect(() => {
    loadObservations();
  }, [loadObservations]);

  // Resize map when tab becomes visible (container was hidden at init)
  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(() => {
      mapRef.current?.resize();
    }, 150);
    return () => clearTimeout(timer);
  }, [isActive]);
  
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };
  
  // Handle delete confirmation
  const handleDeleteClick = (observation: observationsApi.Observation) => {
    setObservationToDelete(observation);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!observationToDelete?.id) return;
    
    setDeleting(true);
    setError(null);
    
    try {
      await observationsApi.deleteObservation(observationToDelete.id);
      setDeleteDialogOpen(false);
      setObservationToDelete(null);
      await loadObservations(); // Reload list
    } catch (err: any) {
      console.error('Error deleting observation:', err);
      setError(err.response?.data?.detail || 'Failed to delete observation');
    } finally {
      setDeleting(false);
    }
  };
  
  // Handle edit button click
  const handleEditClick = (observation: observationsApi.Observation) => {
    if (onEditObservation) {
      onEditObservation(observation);
    }
  };

  // Cadre note handlers - guarded by isAdminOrCadre in the UI
  const handleOpenNoteDialog = (observation: observationsApi.Observation) => {
    setNoteObservation(observation);
    setNoteDraft(observation.cadre_notes || '');
    setNoteError(null);
    setNoteDialogOpen(true);
  };

  const handleCloseNoteDialog = () => {
    if (noteSaving) return;
    setNoteDialogOpen(false);
    setNoteObservation(null);
    setNoteDraft('');
    setNoteError(null);
  };

  const handleSaveCadreNote = async () => {
    if (!noteObservation?.id) return;
    setNoteSaving(true);
    setNoteError(null);
    try {
      const trimmed = noteDraft.trim();
      const updated = await observationsApi.updateCadreNotes(
        noteObservation.id,
        trimmed.length === 0 ? null : trimmed
      );
      // Merge the updated cadre-notes fields into the local list without a full reload
      setObservations((prev) =>
        prev.map((obs) =>
          obs.id === updated.id
            ? {
                ...obs,
                cadre_notes: updated.cadre_notes,
                cadre_notes_by: updated.cadre_notes_by,
                cadre_notes_at: updated.cadre_notes_at,
              }
            : obs
        )
      );
      setNoteDialogOpen(false);
      setNoteObservation(null);
      setNoteDraft('');
    } catch (err: any) {
      console.error('Error saving cadre note:', err);
      setNoteError(err.response?.data?.detail || 'Failed to save cadre note');
    } finally {
      setNoteSaving(false);
    }
  };
  
  // Export observation to PDF
  const handleExportToPDF = async (observation: observationsApi.Observation) => {
    if (!observation.id) return;
    
    setExportingPdf(observation.id);
    
    try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = 20;
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(46, 134, 171);
    doc.text('Surveillance Observation Report', margin, yPosition);
    yPosition += 10;
    
    // Add observation metadata
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`${observation.student_number} - ${observation.detachment}`, margin, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Observation Date: ${formatDateTime(observation.observation_date)}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Submitted: ${formatDateTime(observation.created_at)}`, margin, yPosition);
    yPosition += 10;
    
    // Area Observances Section
    if (observation.area_observances) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'bold');
      doc.text('Area Observances:', margin, yPosition);
      yPosition += 5;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const areaLines = doc.splitTextToSize(observation.area_observances, 175);
      areaLines.forEach((line: string) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 16, yPosition);
        yPosition += 4;
      });
      yPosition += 3;
    }
    
    // Narrative Description Section
    if (observation.narrative_description) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'bold');
      doc.text('Narrative Description:', margin, yPosition);
      yPosition += 5;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const narrativeLines = doc.splitTextToSize(observation.narrative_description, 175);
      narrativeLines.forEach((line: string) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 16, yPosition);
        yPosition += 4;
      });
      yPosition += 5;
    }

    // Cadre Notes Section
    if (observation.cadre_notes) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'bold');
      const attribution = [
        observation.cadre_notes_by || 'Cadre',
        observation.cadre_notes_at ? formatDateTime(observation.cadre_notes_at) : '',
      ].filter(Boolean).join(' · ');
      doc.text(`Cadre Notes${attribution ? ` (${attribution})` : ''}:`, margin, yPosition);
      yPosition += 5;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const cadreLines = doc.splitTextToSize(observation.cadre_notes, 175);
      cadreLines.forEach((line: string) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 16, yPosition);
        yPosition += 4;
      });
      yPosition += 5;
    }
    
    // Vehicle Observations Section
    if (observation.vehicle_observations && observation.vehicle_observations.length > 0) {
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Section header
      doc.setFillColor(46, 134, 171);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text(`Vehicle Observations (${observation.vehicle_observations.length})`, 16, yPosition + 5.5);
      yPosition += 12;
      
      // Vehicle table data
      const vehicleTableData = observation.vehicle_observations.map((vehicle, idx) => {
        const rows: any[] = [];
        
        // Main vehicle row
        rows.push([
          `V${idx + 1}`,
          `${vehicle.vehicle_make || 'N/A'}\n${vehicle.vehicle_model || ''}`.trim(),
          vehicle.vehicle_color || 'N/A',
          vehicle.location_name_address || 'N/A',
          formatDateTime(vehicle.observation_datetime),
        ]);
        
        // Features row if present
        if (vehicle.distinguishing_features) {
          rows.push([
            '',
            { content: `Features: ${vehicle.distinguishing_features}`, colSpan: 4, styles: { fontStyle: 'italic', textColor: [100, 100, 100] } }
          ]);
        }
        
        return rows;
      }).flat();
      
      autoTable(doc, {
        startY: yPosition,
        head: [['#', 'Make/Model', 'Color', 'Location', 'Time']],
        body: vehicleTableData,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [46, 134, 171],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          1: { cellWidth: 35 },
          2: { cellWidth: 25 },
          3: { cellWidth: 'auto' }, // Location takes remaining space
          4: { cellWidth: 38 },
        },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
      });
      
      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }
    
    // Individual Observations Section
    if (observation.individual_observations && observation.individual_observations.length > 0) {
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Section header
      doc.setFillColor(46, 134, 171);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text(`Individual Observations (${observation.individual_observations.length})`, 16, yPosition + 5.5);
      yPosition += 12;
      
      // Individual table data
      const individualTableData = observation.individual_observations.map((individual, idx) => {
        const rows: any[] = [];
        
        // Main individual row
        rows.push([
          `I${idx + 1}`,
          `${individual.gender || 'N/A'}\n${individual.race || ''}`.trim(),
          individual.age_group || 'N/A',
          individual.body_build || 'N/A',
          individual.location_name_address || 'N/A',
          formatDateTime(individual.observation_datetime),
        ]);
        
        // Characteristics row if present
        if (individual.characteristics) {
          rows.push([
            '',
            { content: `Characteristics: ${individual.characteristics}`, colSpan: 5, styles: { fontStyle: 'italic', textColor: [100, 100, 100] } }
          ]);
        }
        
        return rows;
      }).flat();
      
      autoTable(doc, {
        startY: yPosition,
        head: [['#', 'Gender/Race', 'Age', 'Build', 'Location', 'Time']],
        body: individualTableData,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [46, 134, 171],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          1: { cellWidth: 32 },
          2: { cellWidth: 20 },
          3: { cellWidth: 25 },
          4: { cellWidth: 'auto' }, // Location takes remaining space
          5: { cellWidth: 38 },
        },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
      });
    }
    
    // Add footer to all pages
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      
      // Page number only (no CUI marking)
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save PDF
    const dateStr = observation.observation_date ? 
      format(parseISO(observation.observation_date), 'yyyy-MM-dd') : 
      'unknown';
    const fileName = `Observation_${observation.student_number}_${dateStr}.pdf`;
    doc.save(fileName);
    } finally {
      setExportingPdf(null);
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%', overflow: 'hidden' }}>
      {/* Map Section */}
      <Box sx={{
        width: { xs: '100%', md: `${mapWidth}%` },
        height: { xs: '400px', md: '100%' },
        position: 'relative',
        flexShrink: 0,
      }}>
        <ItineraryMapGL
          routeEntries={[]}
          viewMode="view"
          showLegend={true}
          vehicleObservations={filteredVehicleObs}
          individualObservations={filteredIndividualObs}
          flashObservationId={flashTrigger ? flashTrigger.replace(/\.$/, '') : null}
          onMapReady={(map) => { mapRef.current = map; }}
        />
      </Box>
      
      {/* Resizable Divider - Desktop only */}
      {onResizeStart && (
        <Box
          onMouseDown={onResizeStart}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '8px',
            cursor: 'col-resize',
            bgcolor: isDragging ? 'primary.main' : 'divider',
            '&:hover': {
              bgcolor: 'primary.light',
            },
            transition: 'background-color 0.2s',
            flexShrink: 0,
          }}
        />
      )}
      
      {/* List Section */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: { xs: 1.5, sm: 2, md: 3 },
        minWidth: 0,
        maxWidth: '100%',
      }}>
        {/* Header - matches SubmissionsList style */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <AssignmentIcon color="primary" sx={{ fontSize: 32, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} noWrap>
                Submitted Observations
                {selectedObservationId && (
                  <Chip
                    label="Map Filtered"
                    size="small"
                    color="primary"
                    onDelete={clearObservationSelection}
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {observations.length} {observations.length === 1 ? 'observation' : 'observations'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={loadObservations}
                size="small"
                sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter Bar - matches SubmissionsList style */}
        {isAdminOrCadre && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 2,
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}>
            <TextField
              value={studentFilter}
              onChange={(e) => setStudentFilter(e.target.value)}
              size="small"
              placeholder={`Search ${singular.toLowerCase()} (e.g. ${singular}-01)`}
              sx={{ minWidth: 0, flex: '1 1 160px' }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: 'action.active' }} />,
              }}
            />

            {/* Date Range */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 auto', flexWrap: 'wrap' }}>
              <TextField
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                sx={{
                  flex: '1 1 110px',
                  minWidth: 0,
                  '& .MuiInputBase-input': { py: 0.875 },
                }}
              />
              <Typography variant="body2" color="text.secondary">—</Typography>
              <TextField
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                sx={{
                  flex: '1 1 110px',
                  minWidth: 0,
                  '& .MuiInputBase-input': { py: 0.875 },
                }}
                inputProps={{ min: startDate }}
              />
            </Box>

            {/* Quick Range Chips */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip
                label="Today"
                size="small"
                onClick={() => applyQuickRange(1)}
                variant={activeQuickRange === 'today' ? 'filled' : 'outlined'}
                color={activeQuickRange === 'today' ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="7 Days"
                size="small"
                onClick={() => applyQuickRange(7)}
                variant={activeQuickRange === '7d' ? 'filled' : 'outlined'}
                color={activeQuickRange === '7d' ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="30 Days"
                size="small"
                onClick={() => applyQuickRange(30)}
                variant={activeQuickRange === '30d' ? 'filled' : 'outlined'}
                color={activeQuickRange === '30d' ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="All"
                size="small"
                onClick={() => applyQuickRange(null)}
                variant={activeQuickRange === 'all' ? 'filled' : 'outlined'}
                color={activeQuickRange === 'all' ? 'primary' : 'default'}
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            {(studentFilter || startDate || endDate || selectedObservationId) && (
              <Button
                variant="text"
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={() => {
                  setStudentFilter('');
                  setStartDate('');
                  setEndDate('');
                  clearObservationSelection();
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        )}
      
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Empty State */}
      {!loading && !error && observations.length === 0 && (
        <EmptyState
          icon={<AssignmentIcon />}
          title={
            studentFilter || startDate || endDate
              ? 'No observations match the current filters'
              : 'No observations yet'
          }
          description={
            studentFilter || startDate || endDate
              ? 'Try adjusting the search or date range.'
              : 'Submit your first observation to see it here.'
          }
        />
      )}
      
      {/* Observations List */}
      {!loading && !error && observations.length > 0 && (
        <Stack spacing={2}>
          {observations.map((observation) => (
            <Accordion 
              key={observation.id} 
              id={`observation-${observation.id}`}
              defaultExpanded={selectedObservationId === observation.id}
              sx={{
                border: selectedObservationId === observation.id ? '2px solid' : '1px solid',
                borderColor: selectedObservationId === observation.id ? 'primary.main' : 'divider',
                bgcolor: selectedObservationId === observation.id ? 'rgba(46, 134, 171, 0.05)' : 'transparent',
                transition: 'all 0.3s',
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                onClick={() => handleListItemClick(observation)}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    minWidth: 0,
                    overflow: 'hidden',
                  },
                }}
              >
                <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%', minWidth: 0 }}>
                    <AssignmentIcon color="primary" sx={{ mt: 0.25, flexShrink: 0, fontSize: '1.2rem' }} />
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {observation.student_number} - {observation.detachment}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem', display: 'block' }}>
                        {formatDateTime(observation.observation_date)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', flexShrink: 0 }}>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(observation);
                        }}
                        title="Edit observation"
                        sx={{ p: 0.25 }}
                      >
                        <EditIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                      {isAdminOrCadre && (
                        <IconButton
                          size="small"
                          color={observation.cadre_notes ? 'info' : 'default'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenNoteDialog(observation);
                          }}
                          title={observation.cadre_notes ? 'Edit cadre note' : 'Add cadre note'}
                          sx={{ p: 0.25 }}
                        >
                          <RateReviewIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      )}
                      <IconButton 
                        size="small" 
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportToPDF(observation);
                        }}
                        title="Export to PDF"
                        disabled={exportingPdf === observation.id}
                        sx={{ p: 0.25 }}
                      >
                        {exportingPdf === observation.id ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PdfIcon sx={{ fontSize: '1rem' }} />
                        )}
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(observation);
                        }}
                        title="Delete observation"
                        sx={{ p: 0.25 }}
                      >
                        <DeleteIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, ml: 3.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {selectedObservationId === observation.id && (
                      <Tooltip title="Click to show all observations on map">
                        <Chip
                          icon={<LocationIcon />}
                          label="Selected"
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearObservationSelection();
                          }}
                          sx={{
                            fontWeight: 600,
                            height: 22,
                            cursor: 'pointer',
                            '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                            '& .MuiChip-icon': { fontSize: '0.85rem' },
                          }}
                        />
                      </Tooltip>
                    )}
                    <Chip
                      icon={<CarIcon />}
                      label={`${observation.vehicle_observations.length} Vehicle${observation.vehicle_observations.length !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' }, '& .MuiChip-icon': { fontSize: '0.85rem' } }}
                    />
                    <Chip
                      icon={<PersonIcon />}
                      label={`${observation.individual_observations.length} Individual${observation.individual_observations.length !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' }, '& .MuiChip-icon': { fontSize: '0.85rem' } }}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  {/* Vehicle Observations */}
                  {observation.vehicle_observations.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CarIcon fontSize="small" /> Vehicle Observations
                      </Typography>
                      <Stack spacing={2}>
                        {observation.vehicle_observations.map((vehicle, idx) => (
                          <Card 
                            key={vehicle.id} 
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              border: highlightedPointId === `vehicle-${vehicle.id}` ? '2px solid' : '1px solid',
                              borderColor: highlightedPointId === `vehicle-${vehicle.id}` ? 'info.main' : 'divider',
                              bgcolor: highlightedPointId === `vehicle-${vehicle.id}` ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                              transition: 'all 0.2s',
                              '&:hover': { boxShadow: 2, borderColor: 'info.light' },
                            }}
                            onClick={() => handleListItemClick(observation, 'vehicle', idx)}
                          >
                            <CardContent>
                              <Typography variant="body2" fontWeight={600} gutterBottom>
                                Vehicle {idx + 1}
                              </Typography>
                              <Stack spacing={0.5}>
                                {vehicle.vehicle_make && (
                                  <Typography variant="caption">
                                    <strong>Make:</strong> {vehicle.vehicle_make}
                                  </Typography>
                                )}
                                {vehicle.vehicle_model && (
                                  <Typography variant="caption">
                                    <strong>Model:</strong> {vehicle.vehicle_model}
                                  </Typography>
                                )}
                                {vehicle.vehicle_color && (
                                  <Typography variant="caption">
                                    <strong>Color:</strong> {vehicle.vehicle_color}
                                  </Typography>
                                )}
                                {vehicle.distinguishing_features && (
                                  <Typography variant="caption">
                                    <strong>Features:</strong> {vehicle.distinguishing_features}
                                  </Typography>
                                )}
                                {vehicle.location_name_address && (
                                  <Typography variant="caption">
                                    <strong>Location:</strong> {vehicle.location_name_address}
                                  </Typography>
                                )}
                                {vehicle.latitude !== 0 && vehicle.longitude !== 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    <strong>Coordinates:</strong> {vehicle.latitude.toFixed(6)}, {vehicle.longitude.toFixed(6)}
                                  </Typography>
                                )}
                                {vehicle.observation_datetime && (
                                  <Typography variant="caption" color="text.secondary">
                                    <strong>Time:</strong> {formatDateTime(vehicle.observation_datetime)}
                                  </Typography>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  
                  {/* Individual Observations */}
                  {observation.individual_observations.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" /> Individual Observations
                      </Typography>
                      <Stack spacing={2}>
                        {observation.individual_observations.map((individual, idx) => (
                          <Card 
                            key={individual.id} 
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              border: highlightedPointId === `individual-${individual.id}` ? '2px solid' : '1px solid',
                              borderColor: highlightedPointId === `individual-${individual.id}` ? 'warning.main' : 'divider',
                              bgcolor: highlightedPointId === `individual-${individual.id}` ? 'rgba(255, 152, 0, 0.1)' : 'transparent',
                              transition: 'all 0.2s',
                              '&:hover': { boxShadow: 2, borderColor: 'warning.light' },
                            }}
                            onClick={() => handleListItemClick(observation, 'individual', idx)}
                          >
                            <CardContent>
                              <Typography variant="body2" fontWeight={600} gutterBottom>
                                Individual {idx + 1}
                              </Typography>
                              <Stack spacing={0.5}>
                                {individual.gender && (
                                  <Typography variant="caption">
                                    <strong>Gender:</strong> {individual.gender}
                                  </Typography>
                                )}
                                {individual.race && (
                                  <Typography variant="caption">
                                    <strong>Race:</strong> {individual.race}
                                  </Typography>
                                )}
                                {individual.age_group && (
                                  <Typography variant="caption">
                                    <strong>Age:</strong> {individual.age_group}
                                  </Typography>
                                )}
                                {individual.body_build && (
                                  <Typography variant="caption">
                                    <strong>Build:</strong> {individual.body_build}
                                  </Typography>
                                )}
                                {individual.characteristics && (
                                  <Typography variant="caption">
                                    <strong>Characteristics:</strong> {individual.characteristics}
                                  </Typography>
                                )}
                                {individual.location_name_address && (
                                  <Typography variant="caption">
                                    <strong>Location:</strong> {individual.location_name_address}
                                  </Typography>
                                )}
                                {individual.latitude !== 0 && individual.longitude !== 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    <strong>Coordinates:</strong> {individual.latitude.toFixed(6)}, {individual.longitude.toFixed(6)}
                                  </Typography>
                                )}
                                {individual.observation_datetime && (
                                  <Typography variant="caption" color="text.secondary">
                                    <strong>Time:</strong> {formatDateTime(individual.observation_datetime)}
                                  </Typography>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  
                  {/* Area & Narrative */}
                  {(observation.area_observances || observation.narrative_description) && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Additional Information
                      </Typography>
                      {observation.area_observances && (
                        <Typography variant="body2" paragraph>
                          <strong>Area:</strong> {observation.area_observances}
                        </Typography>
                      )}
                      {observation.narrative_description && (
                        <Typography variant="body2">
                          <strong>Narrative:</strong> {observation.narrative_description}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Cadre Notes - visible to everyone, editable only by cadre/admin */}
                  {(observation.cadre_notes || isAdminOrCadre) && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        borderLeft: 3,
                        borderColor: observation.cadre_notes ? 'info.main' : 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <RateReviewIcon sx={{ fontSize: '1rem', color: 'info.main' }} />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Cadre Notes
                          </Typography>
                        </Box>
                        {isAdminOrCadre && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenNoteDialog(observation);
                            }}
                            sx={{ flexShrink: 0 }}
                          >
                            {observation.cadre_notes ? 'Edit' : 'Add'}
                          </Button>
                        )}
                      </Box>
                      {observation.cadre_notes ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                          >
                            {observation.cadre_notes}
                          </Typography>
                          {(observation.cadre_notes_by || observation.cadre_notes_at) && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.75, fontStyle: 'italic' }}
                            >
                              — {observation.cadre_notes_by || 'Cadre'}
                              {observation.cadre_notes_at
                                ? ` · ${formatDateTime(observation.cadre_notes_at)}`
                                : ''}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No cadre notes yet.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Observation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this observation for{' '}
            <strong>{observationToDelete?.student_number}</strong> on{' '}
            <strong>{observationToDelete?.observation_date ? formatDateTime(observationToDelete.observation_date) : ''}</strong>?
            <br /><br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cadre Note Edit Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={handleCloseNoteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {noteObservation?.cadre_notes ? 'Edit Cadre Note' : 'Add Cadre Note'}
          {noteObservation && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {noteObservation.student_number} — {formatDateTime(noteObservation.observation_date)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {noteError && (
            <Alert severity="error" onClose={() => setNoteError(null)} sx={{ mb: 2, mt: 1 }}>
              {noteError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={12}
            label="Notes"
            placeholder="Feedback, coaching comments, follow-up items…"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            disabled={noteSaving}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Saving an empty note will clear any existing cadre note on this observation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoteDialog} disabled={noteSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCadreNote}
            variant="contained"
            disabled={noteSaving}
          >
            {noteSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default ObservationsList;

